/**
 * BackupHistoryList Component
 *
 * Sortable table of backup history with status badges,
 * action buttons (Restore, Verify, Download, Delete), and pagination.
 *
 * Phase 3.7.4: Backup Management UI
 * @module components/backups/BackupHistoryList
 */

import {
  RefreshIcon,
  TrashIcon,
  DownloadIcon,
  CheckCircleIcon,
  AlertIcon,
  UndoIcon,
} from '../common/Icons';
import type { BackupHistoryItem, BackupStatus, BackupType } from '../../types/backup';

interface BackupHistoryListProps {
  items: BackupHistoryItem[];
  total: number;
  limit: number;
  offset: number;
  loading: boolean;
  onPageChange: (offset: number) => void;
  onRestore: (backup: BackupHistoryItem) => void;
  onVerify: (backupId: string) => void;
  onDelete: (backupId: string) => void;
  onRefresh: () => void;
  isSuperAdmin: boolean;
  typeFilter?: string;
  tierFilter?: string;
  onTypeFilterChange?: (value: string) => void;
  onTierFilterChange?: (value: string) => void;
}

function formatBytes(bytes: number | null | undefined): string {
  if (!bytes) return '\u2014';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

// WQ-252: stale-attempt chip predicate.
// Renders a warning when a row was attempted but didn't complete and the
// attempt is older than 24h (chosen as a conservative default — the
// per-row frequency isn't always available on this surface).
const STALE_ATTEMPT_THRESHOLD_MS = 24 * 60 * 60 * 1000;

function isStaleAttempt(item: BackupHistoryItem): boolean {
  if (!item.last_attempted_at) return false;
  if (item.status === 'completed') return false;
  const ageMs = Date.now() - new Date(item.last_attempted_at).getTime();
  return ageMs > STALE_ATTEMPT_THRESHOLD_MS;
}

function StaleAttemptChip() {
  return (
    <span
      data-testid="stale-attempt-chip"
      title="Last attempt did not complete within 24h"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '2px 6px',
        borderRadius: 4,
        fontSize: 10,
        fontWeight: 600,
        marginLeft: 6,
        background: 'rgba(234, 179, 8, 0.15)',
        color: '#eab308',
        border: '1px solid rgba(234, 179, 8, 0.4)',
      }}
    >
      <AlertIcon size={10} /> Stale
    </span>
  );
}

const statusStyles: Record<BackupStatus, { bg: string; color: string; label: string }> = {
  completed: { bg: 'rgba(34, 197, 94, 0.15)', color: '#22c55e', label: 'Completed' },
  in_progress: { bg: 'rgba(59, 130, 246, 0.15)', color: '#3b82f6', label: 'In Progress' },
  pending: { bg: 'rgba(234, 179, 8, 0.15)', color: '#eab308', label: 'Pending' },
  failed: { bg: 'rgba(239, 68, 68, 0.15)', color: '#ef4444', label: 'Failed' },
  cancelled: { bg: 'rgba(107, 114, 128, 0.15)', color: '#6b7280', label: 'Cancelled' },
};

function StatusBadge({ status }: { status: BackupStatus }) {
  const s = statusStyles[status] || statusStyles.pending;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '2px 8px',
        borderRadius: '9999px',
        fontSize: '11px',
        fontWeight: 600,
        background: s.bg,
        color: s.color,
      }}
    >
      {status === 'in_progress' && <RefreshIcon size={10} className="spin" />}
      {status === 'completed' && <CheckCircleIcon size={10} />}
      {status === 'failed' && <AlertIcon size={10} />}
      {s.label}
    </span>
  );
}

const typeLabels: Record<BackupType, string> = {
  scheduled: 'Scheduled',
  manual: 'Manual',
  pre_restore: 'Pre-Restore',
};

function TypeBadge({ type }: { type: BackupType }) {
  return (
    <span
      style={{
        padding: '2px 6px',
        borderRadius: '4px',
        fontSize: '11px',
        background: 'rgba(255, 255, 255, 0.05)',
        color: 'var(--text-secondary)',
      }}
    >
      {typeLabels[type] || type}
    </span>
  );
}

const tierColors: Record<string, string> = {
  daily: '#3b82f6',
  weekly: '#8b5cf6',
  monthly: '#f59e0b',
  on_demand: '#6b7280',
};

const tierLabels: Record<string, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
  on_demand: 'On Demand',
};

function RetentionTierBadge({ tier }: { tier?: string }) {
  if (!tier) return null;
  const color = tierColors[tier] || '#6b7280';
  return (
    <span style={{
      padding: '2px 8px',
      borderRadius: 4,
      fontSize: '0.75rem',
      fontWeight: 500,
      background: `${color}22`,
      color: color,
      border: `1px solid ${color}44`,
    }}>
      {tierLabels[tier] || tier}
    </span>
  );
}

const filterSelectStyle: React.CSSProperties = {
  padding: '4px 8px',
  background: 'rgba(0, 0, 0, 0.2)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  borderRadius: '6px',
  color: 'var(--text-primary)',
  fontSize: '12px',
  cursor: 'pointer',
};

export const BackupHistoryList: React.FC<BackupHistoryListProps> = ({
  items,
  total,
  limit,
  offset,
  loading,
  onPageChange,
  onRestore,
  onVerify,
  onDelete,
  onRefresh,
  isSuperAdmin,
  typeFilter = '',
  tierFilter = '',
  onTypeFilterChange,
  onTierFilterChange,
}) => {
  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(offset / limit) + 1;

  const cellStyle: React.CSSProperties = {
    padding: '8px 10px',
    fontSize: '12px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
    whiteSpace: 'nowrap',
  };

  const headerCellStyle: React.CSSProperties = {
    ...cellStyle,
    color: 'var(--text-secondary)',
    fontWeight: 600,
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '8px',
        }}
      >
        <h4 style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)' }}>
          Backup History ({total})
        </h4>
        <button
          className="btn btn-ghost btn-sm"
          onClick={onRefresh}
          disabled={loading}
          style={{ fontSize: '11px' }}
        >
          <RefreshIcon size={12} className={loading ? 'spin' : ''} /> Refresh
        </button>
      </div>

      {/* Filters */}
      {(onTypeFilterChange || onTierFilterChange) && (
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
          {onTypeFilterChange && (
            <select style={filterSelectStyle} value={typeFilter} onChange={(e) => onTypeFilterChange(e.target.value)}>
              <option value="">All Types</option>
              <option value="scheduled">Scheduled</option>
              <option value="manual">Manual</option>
              <option value="pre_restore">Pre-Restore</option>
            </select>
          )}
          {onTierFilterChange && (
            <select style={filterSelectStyle} value={tierFilter} onChange={(e) => onTierFilterChange(e.target.value)}>
              <option value="">All Tiers</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="on_demand">On Demand</option>
            </select>
          )}
        </div>
      )}

      {items.length === 0 ? (
        <div
          style={{
            textAlign: 'center',
            padding: '24px',
            color: 'var(--text-secondary)',
            fontSize: '13px',
            background: 'rgba(255, 255, 255, 0.02)',
            borderRadius: '8px',
          }}
        >
          No backups yet.
        </div>
      ) : (
        <>
          <div
            style={{
              overflowX: 'auto',
              borderRadius: '8px',
              border: '1px solid rgba(255, 255, 255, 0.06)',
            }}
          >
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'rgba(255, 255, 255, 0.03)' }}>
                  <th style={headerCellStyle}>Date</th>
                  <th style={headerCellStyle}>Last Attempted</th>
                  <th style={headerCellStyle}>Type</th>
                  <th style={headerCellStyle}>Tier</th>
                  <th style={headerCellStyle}>Size</th>
                  <th style={headerCellStyle}>Status</th>
                  <th style={headerCellStyle}>Dest</th>
                  <th style={{ ...headerCellStyle, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr
                    key={item.id}
                    style={{ transition: 'background 0.15s' }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = 'rgba(255, 255, 255, 0.03)')
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = 'transparent')
                    }
                  >
                    <td style={cellStyle}>
                      <span style={{ color: 'var(--text-primary)' }}>
                        {item.started_at
                          ? new Date(item.started_at).toLocaleDateString()
                          : '\u2014'}
                      </span>
                      <br />
                      <span style={{ color: 'var(--text-secondary)', fontSize: '10px' }}>
                        {item.started_at
                          ? new Date(item.started_at).toLocaleTimeString()
                          : ''}
                      </span>
                    </td>
                    <td
                      style={cellStyle}
                      data-testid={`last-attempted-cell-${item.id}`}
                    >
                      {item.last_attempted_at ? (
                        <>
                          <span style={{ color: 'var(--text-primary)' }}>
                            {new Date(item.last_attempted_at).toLocaleDateString()}
                          </span>
                          <br />
                          <span style={{ color: 'var(--text-secondary)', fontSize: '10px' }}>
                            {new Date(item.last_attempted_at).toLocaleTimeString()}
                          </span>
                          {isStaleAttempt(item) && <StaleAttemptChip />}
                        </>
                      ) : (
                        <span style={{ color: 'var(--text-secondary)' }}>{'\u2014'}</span>
                      )}
                    </td>
                    <td style={cellStyle}>
                      <TypeBadge type={item.backup_type} />
                    </td>
                    <td style={cellStyle}>
                      <RetentionTierBadge tier={item.retention_tier} />
                    </td>
                    <td
                      style={{
                        ...cellStyle,
                        color: 'var(--text-primary)',
                        fontFamily: 'monospace',
                        fontSize: '11px',
                      }}
                    >
                      {formatBytes(item.size_bytes)}
                    </td>
                    <td style={cellStyle}>
                      <StatusBadge status={item.status} />
                    </td>
                    <td
                      style={{
                        ...cellStyle,
                        color: 'var(--text-secondary)',
                        fontSize: '11px',
                      }}
                    >
                      {item.destination_type}
                    </td>
                    <td style={{ ...cellStyle, textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                        {item.status === 'completed' && (
                          <>
                            <button
                              className="btn btn-ghost btn-sm"
                              onClick={() => onRestore(item)}
                              title="Restore from this backup"
                              style={{ fontSize: '11px', padding: '2px 6px' }}
                            >
                              <UndoIcon size={12} />
                            </button>
                            <button
                              className="btn btn-ghost btn-sm"
                              onClick={() => onVerify(item.id)}
                              title="Verify integrity"
                              style={{ fontSize: '11px', padding: '2px 6px' }}
                            >
                              <CheckCircleIcon size={12} />
                            </button>
                            {item.destination_type === 'local' && (
                              <button
                                className="btn btn-ghost btn-sm"
                                title="Download (local backups only)"
                                style={{ fontSize: '11px', padding: '2px 6px' }}
                              >
                                <DownloadIcon size={12} />
                              </button>
                            )}
                          </>
                        )}
                        {isSuperAdmin && (
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => onDelete(item.id)}
                          title="Delete backup"
                          style={{
                            fontSize: '11px',
                            padding: '2px 6px',
                            color: 'var(--error, #ef4444)',
                          }}
                        >
                          <TrashIcon size={12} />
                        </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '8px',
                marginTop: '12px',
                fontSize: '12px',
              }}
            >
              <button
                className="btn btn-ghost btn-sm"
                disabled={currentPage <= 1}
                onClick={() => onPageChange(Math.max(0, offset - limit))}
                style={{ fontSize: '11px' }}
              >
                Previous
              </button>
              <span style={{ color: 'var(--text-secondary)' }}>
                Page {currentPage} of {totalPages}
              </span>
              <button
                className="btn btn-ghost btn-sm"
                disabled={currentPage >= totalPages}
                onClick={() => onPageChange(offset + limit)}
                style={{ fontSize: '11px' }}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
